import { BEIGE_400, GRAY_700, WHITE } from 'constants/styling/theme'
import { BorderBoxWrapper, ShadowElevation } from '../BorderBoxWrapper'
import { CSSProperties, FC } from 'react'
import { Stack, Typography, TypographyPropsVariantOverrides } from '@mui/material'

import { OverridableStringUnion } from '@mui/types'
import { Variant } from '@mui/material/styles/createTypography'

/**
 * Props for the {@link NameAvatar} component.
 */
interface Props {
  /** Width and height of the avatar (square) */
  size?: CSSProperties['width']

  /** Background color of the avatar */
  backgroundColor?: CSSProperties['backgroundColor']

  /** Border color of the avatar */
  borderColor?: CSSProperties['borderColor']

  /** Text color used for the initials */
  textColor?: CSSProperties['color']

  /** Full name used to generate initials */
  name?: string

  /** Shadow elevation applied to the avatar container */
  elevation?: ShadowElevation

  /** Typography variant for the initials text */
  textVariant?: OverridableStringUnion<Variant | 'inherit', TypographyPropsVariantOverrides>

  /** Font weight for the initials text */
  fontWeight?: CSSProperties['fontWeight']
}

/**
 * Displays a circular avatar containing initials derived from a name.
 *
 * @remarks
 * - Extracts the first character of up to two words from the provided name
 * - Renders nothing inside if `name` is not provided
 * - Styling is configurable via props with sensible defaults
 */
export const NameAvatar: FC<Props> = ({
  size = '40px',
  backgroundColor = BEIGE_400,
  borderColor = WHITE,
  textColor = GRAY_700,
  elevation = 'md',
  textVariant = 'text-lg',
  fontWeight = 550,
  name,
}) => {
  return (
    <BorderBoxWrapper
      width={size}
      height={size}
      border={`2px solid ${borderColor}`}
      borderRadius={size}
      backgroundColor={backgroundColor}
      elevation={elevation}
    >
      {!!name &&
        <Stack justifyContent="center" alignItems="center" width="100%" height="100%">
          <Typography variant={textVariant} color={textColor} fontWeight={fontWeight}>
            {name.split(' ').splice(0, 2).map((word) => word[0])}
          </Typography>
        </Stack>
      }
    </BorderBoxWrapper>
  )
}
